/**
 * stressDeratingEngine.js — Stress / Derating Analysis
 *
 * OAV-053 (final): Closes the Isograph AWB 4.0 stress-derating capability gap.
 * Implements component derating against published rules of thumb (commercial,
 * aerospace, military) and computes adjusted base failure rates via the
 * Arrhenius temperature-acceleration model and a generalised stress factor.
 *
 * References:
 *   - MIL-HDBK-217F §5 (Part Stress Analysis prediction)
 *   - NASA-STD-8729.1 §4 (Derating for reliability)
 *   - Arrhenius: AF = exp((Ea/k) · (1/T_use - 1/T_stress))
 *   - Generalised stress (electrical): π_S = (S_use / S_rated)^n
 *
 * No dependencies. Pure functions. Inputs in SI; temperatures in Kelvin.
 */

const BOLTZMANN_EV_PER_K = 8.617333262e-5; // eV/K

/**
 * Derating tables — recommended max stress ratios by component class and
 * application category. Values are conservative bounds adopted across industry.
 */
export const DERATING_TABLE = {
  commercial: {  // ground-benign / consumer
    'capacitor-ceramic':       { voltage: 0.80, temperature: 0.80 },
    'capacitor-aluminium':     { voltage: 0.80, temperature: 0.75 },
    'capacitor-tantalum':      { voltage: 0.50, temperature: 0.80 },
    'resistor-film':           { power: 0.70, voltage: 0.80, temperature: 0.85 },
    'resistor-wirewound':      { power: 0.50, temperature: 0.80 },
    'diode-silicon':           { current: 0.70, voltage: 0.70, temperature: 0.85 },
    'transistor-bjt':          { power: 0.70, current: 0.70, voltage: 0.70, temperature: 0.85 },
    'transistor-fet':          { power: 0.70, voltage: 0.80, temperature: 0.85 },
    'ic-cmos':                 { temperature: 0.85, power: 0.80 },
    'inductor':                { current: 0.70, temperature: 0.85 },
    'connector':               { current: 0.50, mating: 0.50 },
  },
  industrial: {  // ground-fixed / process plant — rule-of-thumb 10% stricter
    'capacitor-ceramic':       { voltage: 0.70, temperature: 0.75 },
    'capacitor-aluminium':     { voltage: 0.70, temperature: 0.70 },
    'capacitor-tantalum':      { voltage: 0.40, temperature: 0.75 },
    'resistor-film':           { power: 0.60, voltage: 0.70, temperature: 0.80 },
    'resistor-wirewound':      { power: 0.40, temperature: 0.75 },
    'diode-silicon':           { current: 0.60, voltage: 0.60, temperature: 0.80 },
    'transistor-bjt':          { power: 0.60, current: 0.60, voltage: 0.60, temperature: 0.80 },
    'transistor-fet':          { power: 0.60, voltage: 0.70, temperature: 0.80 },
    'ic-cmos':                 { temperature: 0.80, power: 0.70 },
    'inductor':                { current: 0.60, temperature: 0.80 },
    'connector':               { current: 0.40, mating: 0.40 },
  },
  aerospace: {  // airborne / space — most aggressive derating
    'capacitor-ceramic':       { voltage: 0.60, temperature: 0.65 },
    'capacitor-aluminium':     { voltage: 0.60, temperature: 0.65 },
    'capacitor-tantalum':      { voltage: 0.30, temperature: 0.65 },
    'resistor-film':           { power: 0.50, voltage: 0.60, temperature: 0.70 },
    'resistor-wirewound':      { power: 0.30, temperature: 0.70 },
    'diode-silicon':           { current: 0.50, voltage: 0.50, temperature: 0.70 },
    'transistor-bjt':          { power: 0.50, current: 0.50, voltage: 0.50, temperature: 0.70 },
    'transistor-fet':          { power: 0.50, voltage: 0.60, temperature: 0.70 },
    'ic-cmos':                 { temperature: 0.70, power: 0.60 },
    'inductor':                { current: 0.50, temperature: 0.70 },
    'connector':               { current: 0.30, mating: 0.30 },
  },
};

/**
 * Activation energies (eV) for common failure mechanisms — used by the
 * Arrhenius acceleration factor.
 */
export const ACTIVATION_ENERGIES_EV = {
  'electromigration':            0.70,
  'oxide-breakdown':             0.30,
  'hot-carrier-injection':       0.20,
  'corrosion':                   0.45,
  'thermal-cycling':             0.60,  // approximate for solder joints
  'electrolyte-dryout':          0.94,  // aluminium electrolytic capacitors
  'package-delamination':        0.40,
  'interconnect-fatigue':        0.50,
};

/**
 * Compute the Arrhenius temperature-acceleration factor.
 *  AF = exp((Ea / k) · (1/T_use − 1/T_stress))
 *
 * @param {number} eaEv   Activation energy in electron-volts.
 * @param {number} tUseK  Use (field) temperature in Kelvin.
 * @param {number} tStressK Stress (test) temperature in Kelvin.
 * @returns {number} Acceleration factor (dimensionless).
 */
export function arrheniusAccelerationFactor(eaEv, tUseK, tStressK) {
  if (!(eaEv > 0)) throw new Error('arrheniusAccelerationFactor: Ea must be positive');
  if (!(tUseK > 0) || !(tStressK > 0))
    throw new Error('arrheniusAccelerationFactor: temperatures must be positive Kelvin');
  return Math.exp((eaEv / BOLTZMANN_EV_PER_K) * (1 / tUseK - 1 / tStressK));
}

/**
 * Generalised electrical stress factor (pi_S):
 *   pi_S = (S_use / S_rated)^n        for n ≥ 1 (typical: 2–3)
 *
 * Input clamps S_use ≤ S_rated to avoid an over-stress condition silently
 * producing a "favourable" factor.
 */
export function stressFactor(sUse, sRated, exponent = 2) {
  if (!(sRated > 0)) throw new Error('stressFactor: sRated must be positive');
  if (sUse < 0)      throw new Error('stressFactor: sUse must be non-negative');
  if (sUse > sRated) {
    return null;  // over-stress — caller must treat as failure to comply
  }
  return Math.pow(sUse / sRated, exponent);
}

/**
 * Check a component against the derating table for an application class.
 * Returns {pass, violations:[{stress, limit, actual}, ...]}.
 *
 * @param {object} component  {kind, ratings:{voltage,power,...}, applied:{voltage,power,...}, junctionTempC}
 * @param {string} appClass   commercial | industrial | aerospace
 */
export function checkDerating(component, appClass = 'industrial') {
  const table = DERATING_TABLE[appClass];
  if (!table) throw new Error(`checkDerating: unknown application class "${appClass}"`);
  const limits = table[component.kind];
  if (!limits) {
    return { pass: true, violations: [], note: `No derating limits defined for "${component.kind}" in ${appClass} class` };
  }

  const violations = [];
  const ratings = component.ratings || {};
  const applied = component.applied || {};

  for (const [stress, limit] of Object.entries(limits)) {
    let actual = null;
    if (stress === 'temperature') {
      // For temperature, compare junction T to absolute max T_max (deg C).
      const tMaxC = ratings.tempMaxC;
      const tJC = component.junctionTempC ?? applied.temperature;
      if (tMaxC == null || tJC == null) continue;
      actual = tJC / tMaxC;
    } else {
      const r = ratings[stress];
      const a = applied[stress];
      if (r == null || a == null) continue;
      actual = r > 0 ? a / r : null;
    }
    if (actual != null && actual > limit) {
      violations.push({ stress, limit, actual: Number(actual.toFixed(4)) });
    }
  }
  return { pass: violations.length === 0, violations };
}

/**
 * Apply derating to a component's base failure rate using both the
 * stress-factor and Arrhenius adjustments. Returns the adjusted λ.
 *
 *   λ_adj = λ_base · π_S · AF(Ea, T_use, T_ref)
 *
 * @param {object} input
 *   - lambdaBasePerHour: λ_base (failures/h)
 *   - electricalStressRatio: S_use / S_rated  (0–1)
 *   - electricalStressExponent: n (default 2)
 *   - eaEv: activation energy
 *   - tUseK / tRefK: temperatures
 */
export function adjustFailureRate({
  lambdaBasePerHour,
  electricalStressRatio = 1.0,
  electricalStressExponent = 2,
  eaEv = 0.5,
  tUseK = 313.15,    // 40 °C reference
  tRefK = 298.15,    // 25 °C reference
} = {}) {
  if (!(lambdaBasePerHour > 0))
    throw new Error('adjustFailureRate: lambdaBasePerHour must be positive');
  const piS = electricalStressRatio < 0 ? 0 :
              Math.pow(Math.min(1, electricalStressRatio), electricalStressExponent);
  const af = arrheniusAccelerationFactor(eaEv, tUseK, tRefK);
  return {
    lambdaBasePerHour,
    piS,
    af,
    lambdaAdjustedPerHour: lambdaBasePerHour * piS * af,
  };
}

export default {
  DERATING_TABLE,
  ACTIVATION_ENERGIES_EV,
  arrheniusAccelerationFactor,
  stressFactor,
  checkDerating,
  adjustFailureRate,
};
